import { assert } from '@fe/core';
import { levelToKeyMap } from '../../app/features/settings/components/sub-pages/monitor-alert/common/common';
import {
    AirportThresholdGroupUsage,
    AirportThresholdType,
    CommonAirportThresholdTypes,
    CompanyThresholdsMode,
    MonitorAirportSettingsSet,
    MonitorAirportThreshold,
    MonitorAirportThresholdAlerts,
    MonitorAirportThresholdSettingsItem,
    MonitorAirportThresholdsGroupModel,
    MonitorThresholdAlertsBase,
    MonitorThresholdLevelModel,
    ThresholdEnableState,
    ThresholdInfo,
    ThresholdLevel,
    commonAirportThresholdDefinitions
} from '../../types/thresholds';
import { ThresholdEffectiveBase } from './aircraft-threshold-merger';
import { ThresholdLevelMergeResult, ThresholdLevelMerger, ThresholdValidationErrors } from './threshold-level-merger';
import { ThresholdValidator } from './threshold-validator';

export type AirportThresholdEffective = {
    type: AirportThresholdType;
    alerts: MonitorAirportThresholdAlerts;
} & ThresholdEffectiveBase;

export type AirportThresholdMergeResult = {
    type: AirportThresholdType;
    definition: MonitorAirportThreshold | null;
    inherited: MonitorAirportThreshold | null;
    effective: AirportThresholdEffective | null;
};

type Tab = 'company' | 'user';
type AirportThresholdsData = { user: Omit<MonitorAirportSettingsSet, 'options'>; company: Omit<MonitorAirportSettingsSet, 'options'> };
type GroupHierarchy = { own: MonitorAirportThresholdsGroupModel[]; inherited: MonitorAirportThresholdsGroupModel[] };
export type GroupHierarchyItem = MonitorAirportThresholdsGroupModel & { forceOn?: boolean };

export type AirportThresholdLevelInheritanceInfoItem = {
    name: string;
    // TODO: clean this up. There's an airport groups in here but it is nullable because this interface is used by aircraft thresholds too. Separate aircraft usage from airport.
    group?: Pick<MonitorAirportThresholdsGroupModel, 'airportId' | 'usage' | 'isOff'>;
    level: MonitorThresholdLevelModel | null | undefined;
};

export type AirportThresholdLevelInheritanceInfo = {
    own: AirportThresholdLevelInheritanceInfoItem[];
    inherited: AirportThresholdLevelInheritanceInfoItem[];
};

export class AirportMonitorSettingsMerger {
    private data: AirportThresholdsData | undefined;
    private merger = ThresholdLevelMerger.getForMode(CompanyThresholdsMode.notUsed);
    private companyInheritanceEnabled = false;
    private companyMode: CompanyThresholdsMode = CompanyThresholdsMode.notUsed;
    private definitions: Record<AirportThresholdType, ThresholdInfo> = commonAirportThresholdDefinitions;
    private readonly validator = new ThresholdValidator();

    setMode(mode: CompanyThresholdsMode) {
        this.merger = ThresholdLevelMerger.getForMode(mode);
        this.companyMode = mode;
        this.companyInheritanceEnabled = mode === CompanyThresholdsMode.minimum || mode === CompanyThresholdsMode.template;
    }

    setData(data: AirportThresholdsData) {
        this.data = {
            user: {
                ...data.user,
                groups: addDefault(data.user.groups)
            },
            company: {
                ...data.company,
                groups: addDefault(data.company.groups)
            }
        };
    }
    setThresholdDefinitions(definitions: typeof this.definitions) {
        this.definitions = definitions;
    }

    getData() {
        assert(this.data);
        return this.data;
    }

    getGroups(getFor: Tab): MonitorAirportThresholdsGroupModel[] {
        assert(this.data);

        if (getFor === 'user') {
            if (this.companyInheritanceEnabled) {
                return addCompanyGroupsToUserGroups(addDefault(this.data.user.groups), this.data.company.groups, this.companyMode);
            }

            const user = addDefault(this.data.user.groups);
            // If there's no inheritance enabled, flip all the autogenerated groups to normal groups
            // Here we could remove all of those autogenerated but what is someone flips back company mode to template or minimum
            return user.map((x) => ({
                ...x,
                autoGenerated: false
            }));
        }

        // company
        return addDefault(this.data.company.groups);
    }

    getThresholdsFor(
        getFor: Tab,
        airportId: string,
        usageType: AirportThresholdGroupUsage
    ): Record<AirportThresholdType, AirportThresholdMergeResult> {
        assert(this.data);

        const groupInheritance = this.getGroupInheritanceList(getFor, airportId, usageType);

        return Object.fromEntries(
            CommonAirportThresholdTypes.map((type) => [type, this.getEffective(getFor, type, groupInheritance)])
        ) as Record<AirportThresholdType, AirportThresholdMergeResult>;
    }

    getThresholdFor(
        getFor: Tab,
        type: AirportThresholdType,
        group: Pick<MonitorAirportThresholdsGroupModel, 'airportId' | 'usage'>
    ): AirportThresholdMergeResult {
        assert(this.data);
        const groupInheritance = this.getGroupInheritanceList(getFor, group.airportId, group.usage);
        return this.getEffective(getFor, type, groupInheritance);
    }

    getThresholdOptions(getFor: Tab): MonitorAirportThresholdSettingsItem[] {
        assert(this.data);
        return CommonAirportThresholdTypes.map((type) => {
            const definitionOptions = this.getThresholdSettings(getFor, type);
            const inheritedOptions = this.getInheritedThresholdSettings(getFor, type);
            return {
                type,
                priority: this.merger.mergePriority(definitionOptions?.priority, inheritedOptions?.priority) ?? 50,
                alerts: this.mergeAlerts(definitionOptions?.alerts, inheritedOptions?.alerts)
            };
        });
    }

    getAlertsEditContext(getFor: Tab, type: AirportThresholdType) {
        assert(this.data);
        const definition = this.data[getFor].thresholdsSettings.find((x) => x.type === type);
        const inherited = this.companyInheritanceEnabled ? this.data.company.thresholdsSettings.find((x) => x.type === type) : null;
        const effective = this.mergeAlerts(definition?.alerts, inherited?.alerts);

        return {
            definition: definition?.alerts ?? null,
            inherited: inherited?.alerts ?? null,
            effective
        };
    }

    getThresholdLevelEditContext(
        getFor: Tab,
        type: AirportThresholdType,
        level: ThresholdLevel,
        group: MonitorAirportThresholdsGroupModel
    ): AirportThresholdLevelInheritanceInfo {
        assert(this.data);
        const levelKey = levelToKeyMap[level];
        // TODO: cache group inheritance somewhere. Nah, it's fine...
        const groupInheritance = this.getGroupInheritanceList(getFor, group.airportId, group.usage);
        const thresholdHierarchy = {
            own: groupInheritance.own
                // FIXME: filter out ignored levels or indicate them as ignored in the UI
                .map((x) => ({ name: this.groupToName(x), group: x, level: x.thresholds.find((x) => x.type === type)?.[levelKey] })),
            inherited: groupInheritance.inherited
                // FIXME: filter out ignored levels or indicate them as ignored in the UI
                .map((x) => ({ name: this.groupToName(x), group: x, level: x.thresholds.find((x) => x.type === type)?.[levelKey] }))
        };

        return thresholdHierarchy;
    }

    private groupToName(group: MonitorAirportThresholdsGroupModel): string {
        return (group.airportId || 'Default') + ' - ' + AirportThresholdGroupUsage[group.usage];
    }

    private getEffective(getFor: Tab, type: AirportThresholdType, groupHierarchy: GroupHierarchy): AirportThresholdMergeResult {
        const info = this.definitions[type];
        // Definition is always the first item in the list
        const definition = groupHierarchy.own[0].thresholds.find((x) => x.type === type) ?? null;
        const definitionOptions = this.getThresholdSettings(getFor, type);
        const inheritedOptions = this.getInheritedThresholdSettings(getFor, type);
        const thresholdHierarchy = this.getThresholdHierarchy(type, groupHierarchy);

        const ownEnableState = this.resolveEnableState(thresholdHierarchy.own);
        const inheritedEnableState = this.resolveEnableState(thresholdHierarchy.inherited);

        const result: AirportThresholdMergeResult = {
            type,
            definition: definition,
            // find what this threshold directly inherits from, it will either be first from its own group or from the other group
            // when going through our own group make sure to skip the definition of this threshold

            // FIXME: This is a bit problematic, because inherited in this case is only used to resolve enabled status, priority and alerts
            // Each level can resolve to a level from a different thresholds from the hierarchy
            // So we can't use inherited to validate company minimums
            inherited: thresholdHierarchy.own.find((x) => x !== definition) ?? thresholdHierarchy.inherited.find((x) => x) ?? null,
            effective: {
                type: type,
                priority: this.merger.mergePriority(definitionOptions?.priority, inheritedOptions?.priority) ?? 50,
                alerts: this.mergeAlerts(definitionOptions?.alerts, inheritedOptions?.alerts),
                enableState: this.merger.mergeEnableStatus(ownEnableState, inheritedEnableState),
                info: this.mergeLevel(info, definition, thresholdHierarchy, ThresholdLevel.info),
                watch: this.mergeLevel(info, definition, thresholdHierarchy, ThresholdLevel.watch),
                warning: this.mergeLevel(info, definition, thresholdHierarchy, ThresholdLevel.warning),
                critical: this.mergeLevel(info, definition, thresholdHierarchy, ThresholdLevel.critical),
                emergency: this.mergeLevel(info, definition, thresholdHierarchy, ThresholdLevel.emergency)
            }
        };

        if (result.effective) {
            this.validator.validateThreshold(result.effective, info);
        }

        return result;
    }

    private mergeLevel(
        info: ThresholdInfo,
        definition: MonitorAirportThreshold | null,
        hierarchy: { own: MonitorAirportThreshold[]; inherited: MonitorAirportThreshold[] },
        level: ThresholdLevel
    ): ThresholdLevelMergeResult {
        const levelKey = levelToKeyMap[level];
        const definedLevel = definition?.[levelKey];
        const resolvedOwnLevel = this.resolveLevel(hierarchy.own, levelKey, level);
        const resolvedAlternativeOwnLevel = this.resolveLevel(hierarchy.own, levelKey, level, definition);
        const resolvedInheritedLevel = this.resolveLevel(hierarchy.inherited, levelKey, level);
        const mergedLevel = this.merger.mergeLevels(resolvedOwnLevel, resolvedInheritedLevel, info);

        // First check if this threshold has defined it's own level
        const levelDefined = !!definedLevel;

        // here we need to determine if we could've resolved to some other level if we skipped definition of this level
        // this way we know that we're overriding some of our own levels
        // from inherited levels we only care about the final resolved level
        const levelDefinedInHierarchy =
            (resolvedAlternativeOwnLevel !== null && resolvedAlternativeOwnLevel !== definedLevel) || resolvedInheritedLevel !== null;
        mergedLevel.isOverride = levelDefined && levelDefinedInHierarchy;

        if (!this.validator.validateLevelMinimum(resolvedOwnLevel, resolvedInheritedLevel, info)) {
            mergedLevel.errors |= ThresholdValidationErrors.companyMinimumNotMet;
        }
        return mergedLevel;
    }

    private getThresholdHierarchy(
        type: AirportThresholdType,
        groupHierarchy: GroupHierarchy
    ): { own: MonitorAirportThreshold[]; inherited: MonitorAirportThreshold[] } {
        return {
            own: groupHierarchy.own
                .filter((x) => !x.isOff)
                .map((x) => x.thresholds.find((x) => x.type === type))
                .filter(Boolean) as MonitorAirportThreshold[],
            inherited: groupHierarchy.inherited
                .filter((x) => !x.isOff)
                .map((x) => x.thresholds.find((x) => x.type === type))
                .filter(Boolean) as MonitorAirportThreshold[]
        };
    }

    private resolveEnableState(thresholds: MonitorAirportThreshold[]): ThresholdEnableState {
        for (const item of thresholds) {
            // If we hit first enable status other than not set that's the enable state
            if (item.enableState !== ThresholdEnableState.notSet) {
                return item.enableState;
            }
        }

        return ThresholdEnableState.notSet;
    }

    private resolveLevel(
        thresholds: MonitorAirportThreshold[],
        key: (typeof levelToKeyMap)[ThresholdLevel],
        level: ThresholdLevel,
        skip?: MonitorAirportThreshold | null
    ): MonitorThresholdLevelModel | null {
        let resolved: MonitorThresholdLevelModel | null = null;
        for (const threshold of thresholds) {
            if (threshold === skip) {
                continue;
            }

            const iLevel = threshold[key];
            if (threshold.ignoredInheritedLevels.includes(level) && !iLevel) {
                break;
            }

            if (!iLevel) {
                continue;
            }

            resolved ??= {
                repeat: null,
                triggerOnImproving: null,
                // Keep the values below to be the same as first non null level
                value: iLevel.value,
                unitIndex: iLevel.unitIndex,
                actionType: iLevel.actionType,
                actionTimeMinutes: iLevel.actionTimeMinutes
            } satisfies MonitorThresholdLevelModel;

            // Null means inherit here from previous level
            resolved.repeat ??= iLevel.repeat;
            resolved.triggerOnImproving ??= iLevel.triggerOnImproving;
        }
        return resolved;
    }

    private getGroupInheritanceList(getFor: Tab, airportId: string, type: AirportThresholdGroupUsage): GroupHierarchy {
        assert(this.data);
        const own = buildGroupInheritance(getFor, airportId, type, this.data);
        const includeInherited = getFor === 'user' && this.companyInheritanceEnabled;
        const inherited = includeInherited ? buildGroupInheritance('company', airportId, type, this.data) : [];
        return { own, inherited };
    }

    private getThresholdSettings(getFor: Tab, type: AirportThresholdType): MonitorAirportThresholdSettingsItem | null {
        return this.data![getFor].thresholdsSettings.find((x) => x.type === type) ?? null;
    }

    private getInheritedThresholdSettings(getFor: Tab, type: AirportThresholdType): MonitorAirportThresholdSettingsItem | null {
        // Company inherits nothing
        if (getFor === 'company') {
            return null;
        }

        if (!this.companyInheritanceEnabled) {
            return null;
        }

        return this.data!.company.thresholdsSettings.find((x) => x.type === type) ?? null;
    }

    protected mergeAlerts(
        alerts: MonitorThresholdAlertsBase | undefined,
        inherited: MonitorThresholdAlertsBase | undefined
    ): MonitorThresholdAlertsBase {
        return {
            eventList: this.merger.mergeAlertBooleans(alerts?.eventList, inherited?.eventList),
            eventListAck: this.merger.mergeAlertBooleans(alerts?.eventListAck, inherited?.eventListAck),
            eventListImportant: this.merger.mergeAlertBooleans(alerts?.eventListImportant, inherited?.eventListImportant),
            email: this.merger.mergeAlertBooleans(alerts?.email, inherited?.email),
            logFile: this.merger.mergeAlertBooleans(alerts?.logFile, inherited?.logFile),
            popup: this.merger.mergeAlertBooleans(alerts?.popup, inherited?.popup),
            osAlert: this.merger.mergeAlertBooleans(alerts?.osAlert, inherited?.osAlert),
            overrideColor: this.merger.mergeAlertBooleans(alerts?.overrideColor, inherited?.overrideColor),
            voice: this.merger.mergeAlertBooleans(alerts?.voice, inherited?.voice),
            playSound: this.merger.mergeAlertBooleans(alerts?.playSound, inherited?.playSound),
            overrideColorHex: this.merger.mergeAlertStrings(alerts?.overrideColorHex, inherited?.overrideColorHex)
        };
    }
}

function buildGroupInheritance(set: Tab, airportId: string, type: AirportThresholdGroupUsage, data: AirportThresholdsData) {
    const list: MonitorAirportThresholdsGroupModel[] = [];

    const addToList = (id: string, usageType: AirportThresholdGroupUsage) => {
        const item = data![set].groups.find((x) => x.airportId === id && x.usage === usageType);
        if (item) {
            list.unshift(item);
        }
    };

    // everything inherits from [default - default]
    addToList('', AirportThresholdGroupUsage.default);

    // non default usage means we need to add [default - type]
    if (type !== AirportThresholdGroupUsage.default) {
        addToList('', type);
    }

    // Named airport
    if (airportId) {
        // [named - default]
        addToList(airportId, AirportThresholdGroupUsage.default);

        // if we are non default usage add [named - type]
        if (type !== AirportThresholdGroupUsage.default) {
            addToList(airportId, type);
        }
    }

    return list;
}

function addDefault(group: MonitorAirportThresholdsGroupModel[]): MonitorAirportThresholdsGroupModel[] {
    if (!group.some((x) => x.airportId === '' && x.usage === AirportThresholdGroupUsage.default)) {
        return [
            {
                airportId: '',
                usage: AirportThresholdGroupUsage.default,
                autoGenerated: false,
                isOff: false,
                thresholds: []
            },
            ...group
        ];
    }
    return group;
}

function addCompanyGroupsToUserGroups(
    userGroups: MonitorAirportThresholdsGroupModel[],
    companyGroups: MonitorAirportThresholdsGroupModel[],
    mode: CompanyThresholdsMode
): GroupHierarchyItem[] {
    // IMPROVE: use immer ugh..
    const grouped = Object.groupBy(userGroups, (x) => x.airportId) as Record<string, GroupHierarchyItem[]>;
    for (const companyGroup of companyGroups) {
        // If not part of the user group add it
        if (!(companyGroup.airportId in grouped)) {
            grouped[companyGroup.airportId] = [
                {
                    airportId: companyGroup.airportId,
                    usage: companyGroup.usage,
                    autoGenerated: true,
                    isOff: false,
                    forceOn: true,
                    thresholds: []
                }
            ];
        } else {
            // If this usage is not part of the user group add it
            const idx = grouped[companyGroup.airportId]!.findIndex((x) => x.usage === companyGroup.usage);
            if (idx === -1) {
                grouped[companyGroup.airportId]!.push({
                    airportId: companyGroup.airportId,
                    usage: companyGroup.usage,
                    autoGenerated: true,
                    isOff: false,
                    forceOn: true,
                    thresholds: []
                });
            } else {
                if (mode === CompanyThresholdsMode.minimum) {
                    // The usage is already part of user group
                    // In that case force update isOff to false because every company usage should be enabled on the user side, and mark it as forced on
                    const item = grouped[companyGroup.airportId]![idx];
                    grouped[companyGroup.airportId]![idx] = {
                        ...item,
                        isOff: false,
                        forceOn: true
                    };
                }
            }
        }
    }

    for (const group of Object.values(grouped)) {
        if (group.length === 0) {
            continue;
        }

        // Make sure there's always a default usage in each group
        if (!group.find((x) => x.usage === AirportThresholdGroupUsage.default)) {
            group.push({
                airportId: group[0].airportId,
                usage: AirportThresholdGroupUsage.default,
                autoGenerated: true,
                isOff: false,
                forceOn: true,
                thresholds: []
            });
        }
    }

    return Object.values(grouped).flat() as GroupHierarchyItem[];
}
